<template>
  <div class="modal fade" id="companyModal" tabindex="-1" data-bs-keyboard="false" data-bs-backdrop="static">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ isEditMode ? 'Editar Empresa' : 'Nova Empresa' }}
          </h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>

        <div class="modal-body">
          <form @submit.prevent="submit">
            <div class="row mb-3">
              <div class="col-md-3">
                <label class="form-label required">CNPJ</label>
                <div class="input-group">
                  <input type="text" class="form-control form-control-solid" v-model="form.company_cnpj" required
                         v-mask="'##.###.###/####-##'" placeholder="00.000.000/0000-00"
                         @blur="fetchCnpjData"
                         :disabled="cnpjLoading" />
                  <span v-if="cnpjLoading" class="input-group-text">
                    <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                  </span>
                </div>
                <small v-if="cnpjError" class="text-danger">{{ cnpjError }}</small>
              </div>
              <div class="col-md-6">
                <label class="form-label required">Nome da Empresa</label>
                <input type="text" class="form-control form-control-solid" v-model="form.company_name" required
                       placeholder="Razão Social ou Nome Fantasia" />
              </div>
              <div class="col-md-3">
                <label class="form-label required">Telefone</label>
                <input type="text" class="form-control form-control-solid" v-model="form.company_phone" required
                       v-mask="['(##) ####-####', '(##) #####-####']" placeholder="(00) 00000-0000" />
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-md-6">
                <label class="form-label required">E-mail</label>
                <input type="email" class="form-control form-control-solid" v-model="form.company_email" required
                       placeholder="contato@empresa.com" />
              </div>
              <div class="col-md-6">
                <label class="form-label">Representante Legal</label>
                <input type="text" class="form-control form-control-solid" v-model="form.company_legal_representative"
                       placeholder="Nome do representante legal" />
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-md-6">
                <label class="form-label required">Dia de Pagamento (1-28)</label>
                <input type="number" class="form-control form-control-solid" v-model.number="form.payment_day"
                       required min="1" max="28" placeholder="1 a 28" />
              </div>
            </div>

            <!-- Dados do contrato (somente na inclusão) -->
            <template v-if="!isEditMode">
              <h6 class="text-muted mb-2 fw-bold mt-3">DADOS DO CONTRATO</h6>
              <div class="row mb-3">
                <div class="col-md-12">
                  <label class="form-label">Plano Vinculado</label>
                  <select v-model="form.id_plan" class="form-control form-control-solid">
                    <option :value="null">Selecione um plano</option>
                    <option :value="plan.id_plan" v-for="plan in corporatePlans" :key="plan.id_plan">
                      {{ plan.plan_name }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="row mb-3">
                <div class="col-md-6">
                  <label class="form-label">Data início do contrato</label>
                  <Calendar class="form-control form-control-sm w-100"
                            dateFormat="dd/mm/yy"
                            v-model="form.contract_start_date"
                            showButtonBar />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Data término do contrato</label>
                  <Calendar class="form-control form-control-sm w-100"
                            dateFormat="dd/mm/yy"
                            v-model="form.contract_valid_until"
                            showButtonBar />
                </div>
              </div>
              <div class="row mb-3">
                <div class="col-md-6">
                  <label class="form-label">Valor por titular</label>
                  <money3 class="form-control form-control-solid"
                         v-model="form.subs_base_price"
                         v-bind="moneyConfig" />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Valor por dependente</label>
                  <money3 class="form-control form-control-solid"
                         v-model="form.subs_dependent_price"
                         v-bind="moneyConfig" />
                </div>
              </div>
            </template>

            <hr class="my-3">

            <div class="row mb-3">
              <div class="col-md-2">
                <label class="form-label required">CEP</label>
                <input type="text" class="form-control form-control-solid" v-model="form.company_addr_zipcode"
                       required v-mask="'#####-###'" @change="getZipCode($event.target.value)"
                       placeholder="00000-000" />
              </div>
              <div class="col-md-5">
                <label class="form-label required">Endereço</label>
                <input type="text" class="form-control form-control-solid" v-model="form.company_address"
                       required placeholder="Rua, Avenida..." />
              </div>
              <div class="col-md-2">
                <label class="form-label required">Número</label>
                <input type="text" class="form-control form-control-solid" v-model="form.company_addr_number"
                       required ref="addr_number" placeholder="Nro" />
              </div>
              <div class="col-md-3">
                <label class="form-label">Complemento</label>
                <input type="text" class="form-control form-control-solid" v-model="form.company_addr_compl"
                       placeholder="Sala, Andar..." />
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-md-4">
                <label class="form-label required">Bairro</label>
                <input type="text" class="form-control form-control-solid" v-model="form.company_neighborhood"
                       required placeholder="Bairro" />
              </div>
              <div class="col-md-4">
                <label class="form-label required">Estado</label>
                <DropdownUbbi
                  :showClear="true"
                  :options="states"
                  :filter="true"
                  appendTo="self"
                  @change="getCitiesFromState($event.value)"
                  v-model="form.id_state"
                  optionLabel="state_name"
                  optionValue="id_state"
                  placeholder="Selecione o estado"
                />
              </div>
              <div class="col-md-4">
                <label class="form-label required">Cidade</label>
                <DropdownUbbi
                  :showClear="true"
                  :options="citiesFromState"
                  :filter="true"
                  appendTo="self"
                  v-model="form.id_city"
                  optionLabel="city_name"
                  optionValue="id_city"
                  placeholder="Selecione a cidade"
                />
              </div>
            </div>

            <hr class="my-3">

            <div class="row mb-3">
              <div class="col-md-4">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" v-model="form.company_active" id="companyActive">
                  <label class="form-check-label" for="companyActive">Empresa ativa</label>
                </div>
              </div>
            </div>

            <div class="modal-footer px-0 pb-0">
              <button type="button" class="btn btn-secondary" @click="closeModal">Cancelar</button>
              <button type="submit" class="btn btn-primary">
                {{ isEditMode ? 'Atualizar' : 'Salvar' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/helpers/http";
import Modal from "@/helpers/modal";
import Alert from "@/helpers/alert";
import Loader from "@/helpers/loader";
import DropdownUbbi from "@/components/DropdownUbbi.vue";
import { mask } from "vue-the-mask";
import { CompanyService } from "@/service/companyService";
import Calendar from "primevue/calendar";
import { Money3 } from "v-money3";
import moment from "moment";

export default {
  name: "CompanyModal",
  props: ['company'],
  emits: ['onClose', 'companySaved'],
  components: { DropdownUbbi, Calendar, Money3 },
  directives: { mask },
  data() {
    return {
      states: [],
      citiesFromState: [],
      corporatePlans: [],
      companyService: CompanyService.new(),
      form: this.getEmptyForm(),
      cnpjLoading: false,
      cnpjError: null,
      moneyConfig: {
        masked: false,
        prefix: 'R$ ',
        suffix: '',
        thousands: '.',
        decimal: ',',
        precision: 2,
        disableNegative: true,
        min: null,
        max: null,
        allowBlank: true,
        minimumNumberOfCharacters: 0,
        shouldRound: false,
        focusOnRight: false,
      },
    }
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    isEditMode() {
      return this.company !== null;
    },
  },
  watch: {
    company: {
      immediate: true,
      handler(newVal) {
        this.cnpjError = null;
        this.cnpjLoading = false;
        if (newVal) {
          this.populateForm(newVal);
        } else {
          this.form = this.getEmptyForm();
        }
      }
    },
    'form.id_plan'() {
      this.applyPlanPricesToForm();
    },
    corporatePlans: {
      handler() {
        this.applyPlanPricesToForm();
      },
      deep: true
    },
    'form.contract_start_date'(startDate) {
      if (startDate) {
        const end = moment(startDate).add(12, 'months').subtract(1, 'day').toDate();
        this.form.contract_valid_until = end;
      }
    }
  },
  mounted() {
    this.getStates();
    this.loadCorporatePlans();
  },
  methods: {
    getEmptyForm() {
      return {
        company_name: null,
        company_cnpj: null,
        company_email: null,
        company_legal_representative: null,
        company_phone: null,
        company_address: null,
        company_addr_zipcode: null,
        company_addr_number: null,
        company_addr_compl: null,
        company_neighborhood: null,
        id_city: null,
        id_state: null,
        id_plan: null,
        payment_day: 10,
        company_active: true,
        contract_start_date: null,
        contract_valid_until: null,
        subs_base_price: null,
        subs_dependent_price: null,
      };
    },
    applyPlanPricesToForm() {
      if (this.isEditMode || !this.form.id_plan || !this.corporatePlans.length) return;
      const plan = this.corporatePlans.find(p => p.id_plan === this.form.id_plan);
      if (plan) {
        this.form.subs_base_price = plan.plan_base_price != null ? Number(plan.plan_base_price) : null;
        this.form.subs_dependent_price = plan.plan_dependent_price != null ? Number(plan.plan_dependent_price) : null;
      }
    },
    populateForm(company) {
      this.form = {
        company_name: company.company_name,
        company_cnpj: company.company_cnpj,
        company_email: company.company_email,
        company_legal_representative: company.company_legal_representative ?? null,
        company_phone: company.company_phone,
        company_address: company.company_address,
        company_addr_zipcode: company.company_addr_zipcode,
        company_addr_number: company.company_addr_number,
        company_addr_compl: company.company_addr_compl,
        company_neighborhood: company.company_neighborhood,
        id_city: company.id_city,
        id_state: company.city?.id_state ?? null,
        id_plan: null,
        payment_day: company.payment_day ?? 10,
        company_active: company.company_active,
      };

      if (this.form.id_state) {
        this.getCitiesFromState(this.form.id_state);
      }
    },
    loadCorporatePlans() {
      http.get(`/places/${this.place.id_place}/plans`, {
        params: { plan_company: true, per_page: 1000 }
      }).then((response) => {
        const raw = response.data?.data ?? response.data;
        this.corporatePlans = Array.isArray(raw) ? raw : [];
      }).catch(() => {
        this.corporatePlans = [];
      });
    },
    submit() {
      Loader.show('Salvando empresa...');

      const { contract_start_date, contract_valid_until, ...restForm } = this.form;
      const payload = {
        ...restForm,
        ...(!this.isEditMode ? {
          id_plan: this.form.id_plan ?? null,
          start_date: this.form.contract_start_date instanceof Date
            ? moment(this.form.contract_start_date).format('YYYY-MM-DD')
            : (this.form.contract_start_date || null),
          valid_until: this.form.contract_valid_until instanceof Date
            ? moment(this.form.contract_valid_until).format('YYYY-MM-DD')
            : (this.form.contract_valid_until || null),
          subs_base_price: this.form.subs_base_price != null && this.form.subs_base_price !== ''
            ? Number(this.form.subs_base_price)
            : null,
          subs_dependent_price: this.form.subs_dependent_price != null && this.form.subs_dependent_price !== ''
            ? Number(this.form.subs_dependent_price)
            : null,
        } : {})
      };

      const promise = this.isEditMode
        ? this.companyService.update(this.place.id_place, this.company.id_company, payload)
        : this.companyService.store(this.place.id_place, payload);

      promise
        .then(() => {
          Loader.close();
          Alert.success(this.isEditMode ? 'Empresa atualizada com sucesso.' : 'Empresa criada com sucesso.');
          this.$emit('companySaved');
          this.closeModal();
        })
        .catch((error) => {
          Loader.close();
          Alert.error(error.response?.data?.message || 'Erro ao salvar empresa.');
        });
    },
    getCnpjDigits() {
      return (this.form.company_cnpj || '').replace(/\D/g, '');
    },
    async fetchCnpjData() {
      const digits = this.getCnpjDigits();
      if (digits.length !== 14) {
        this.cnpjError = null;
        return;
      }
      this.cnpjLoading = true;
      this.cnpjError = null;
      try {
        const { data } = await http.get(`cnpj/${digits}`);
        this.form.company_name = data.razao_social || data.nome_fantasia || this.form.company_name;
        this.form.company_email = data.email || this.form.company_email;
        this.form.company_legal_representative = data.representante_legal ?? this.form.company_legal_representative;
        const tel = data.telefones?.[0];
        if (tel && tel.ddd && tel.numero) {
          const n = String(tel.numero).replace(/\D/g, '');
          this.form.company_phone = n.length === 9
            ? `(${tel.ddd}) ${n.slice(0, 5)}-${n.slice(5)}`
            : `(${tel.ddd}) ${n.slice(0, 4)}-${n.slice(4)}`;
        }
        this.form.company_address = data.logradouro ?? this.form.company_address;
        this.form.company_addr_number = data.numero ?? this.form.company_addr_number;
        this.form.company_addr_compl = data.complemento ?? this.form.company_addr_compl;
        this.form.company_neighborhood = data.bairro ?? this.form.company_neighborhood;
        if (data.cep) {
          const cep = data.cep.replace(/\D/g, '');
          this.form.company_addr_zipcode = cep.length === 8 ? `${cep.slice(0, 5)}-${cep.slice(5)}` : data.cep;
        }
        const uf = (data.uf || '').toUpperCase();
        if (uf && this.states.length) {
          const state = this.states.find(s => (s.state_initials || '').toUpperCase() === uf);
          if (state) {
            this.form.id_state = state.id_state;
            await this.getCitiesFromState(state.id_state);
            const municipio = (data.municipio || '').trim();
            if (municipio && this.citiesFromState.length) {
              const city = this.citiesFromState.find(
                c => (c.city_name || '').toUpperCase().trim() === municipio.toUpperCase()
              );
              if (city) this.form.id_city = city.id_city;
            }
          }
        }
      } catch (err) {
        this.cnpjError = err.response?.status === 404
          ? 'CNPJ não encontrado.'
          : 'Não foi possível buscar os dados do CNPJ.';
      } finally {
        this.cnpjLoading = false;
      }
    },
    getZipCode(zipcode) {
      http.get(`zipcode/${zipcode}`).then(async (response) => {
        this.form.company_address = response.data.address;
        this.form.company_neighborhood = response.data.neighborhood;
        this.form.id_state = response.data.state?.id_state;
        await this.getCitiesFromState(this.form.id_state);
        this.form.id_city = response.data.city?.id_city;
        this.$refs.addr_number?.focus();
      }).catch(() => {});
    },
    getStates() {
      http.get('/states').then((response) => {
        this.states = response.data;
      });
    },
    getCitiesFromState(stateId) {
      if (!stateId) {
        this.citiesFromState = [];
        return Promise.resolve();
      }
      return http.get(`/states/${stateId}/cities`).then((response) => {
        this.citiesFromState = response.data;
      });
    },
    closeModal() {
      Modal.toggle('#companyModal');
      this.$emit('onClose');
      this.form = this.getEmptyForm();
    },
  }
}
</script>
